import { getRandomColor } from 'module/helpers/get-random-color';
import { useTranslate } from 'module/hooks/use-translation';
import { Select } from 'module/ui/components/atoms/select';
import { useNativePermissionSetDefinitionsLightQuery } from 'queries/permission';
import { useGetAllUserGroupsQuery } from 'queries/user-group';
import { FC, useCallback, useMemo } from 'react';

interface Props {
  value?: {
    userGroupIds: number[];
    permissionSetIds: number[];
  };
  onChange?: (value?: {
    userGroupIds: number[];
    permissionSetIds: number[];
  }) => void;
}

export const PermissionsSelect: FC<Props> = ({ value, onChange }) => {
  const { data: permissionSetDefinitionsLight } =
    useNativePermissionSetDefinitionsLightQuery();
  const { data: userGroups } = useGetAllUserGroupsQuery();
  const translate = useTranslate();
  const options = useMemo(() => {
    return [
      {
        label: translate(
          'knowledge_base.folder.create.permissions.groups.anyone',
        ),
        value: -1,
        color: 'gray',
      },
      {
        label: translate(
          'knowledge_base.folder.create.permissions.groups.permission',
        ),
        options: permissionSetDefinitionsLight
          ?.filter(
            (permission) =>
              permission.permissionSetObjectType === 'ACCOUNT' &&
              permission.permissionSetSpecificTo === 'NATIVE',
          )
          ?.map((permission) => ({
            label: permission.permissionSetName,
            value: permission.permissionSetId,
            type: 'permission',
            color: getRandomColor(permission.permissionSetId),
          })),
      },
      {
        label: translate(
          'knowledge_base.folder.create.permissions.groups.user_group',
        ),
        options: userGroups?.map((userGroup) => ({
          label: userGroup.groupName,
          value: userGroup.groupId,
          type: 'user_group',
          color: getRandomColor(userGroup.groupId),
        })),
      },
    ];
  }, [permissionSetDefinitionsLight, userGroups, translate]);
  const selectedOptions = useMemo(() => {
    if (!value) {
      return {
        label: translate(
          'knowledge_base.folder.create.permissions.groups.anyone',
        ),
        value: -1,
      };
    }
    const groupOptions = options[2].options;
    const permissionOptions = options[1].options;
    const selectedGroupOptions =
      groupOptions?.filter((option) =>
        value?.userGroupIds.includes(option.value),
      ) ?? [];
    const selectedPermissionOptions =
      permissionOptions?.filter((option) =>
        value?.permissionSetIds.includes(option.value),
      ) ?? [];

    return [...selectedGroupOptions, ...selectedPermissionOptions];
  }, [options, translate, value]);
  const handleChange = useCallback(
    (values) => {
      const anyoneOption =
        values.find((option) => option.value === -1) && values.length === 1;

      if (anyoneOption || values.length === 0) {
        onChange?.(undefined);

        return;
      }
      onChange?.({
        userGroupIds: values
          .filter((option) => option.type === 'user_group')
          .map((option) => option.value),
        permissionSetIds: values
          .filter((option) => option.type === 'permission')
          .map((option) => option.value),
      });
    },
    [onChange],
  );

  return (
    <Select
      options={[options[1], options[2]]}
      type="tag"
      isGrouped
      isMulti
      hideSelectedOptions={false}
      value={selectedOptions}
      onChange={handleChange}
    />
  );
};
